import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, ArrowRight, BookOpen, Lock, Zap, Inbox, FileText } from "lucide-react";
import PendingRatingsList from "@/components/PendingRatingsList";
import VendorScorecardCard from "@/components/VendorScorecardCard";
import PushToggle from "@/components/PushToggle";
import ReferralsCard from "@/components/ReferralsCard";
import NonprofitStatusCard from "@/components/NonprofitStatusCard";
import AIRecommendationsPanel from "@/components/AIRecommendationsPanel";

function ProBadge({ testId }) {
  return (
    <span
      data-testid={testId}
      className="ml-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0 text-[10px] font-medium tracking-wider uppercase"
      style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
    >
      <Lock className="h-2.5 w-2.5" /> Pro
    </span>
  );
}

function statusClass(s) {
  return {
    pending: "status-pending",
    approved: "status-approved",
    rejected: "status-rejected",
    cancelled: "status-rejected",
    waitlisted: "status-pending",
  }[s] || "status-pending";
}

export default function VendorDashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);
  const [inquiries, setInquiries] = useState({ unread: 0, total: 0, loaded: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/applications/mine");
      setApps(data);
    } finally { setLoading(false); }
  };

  // Lightweight inquiries poll — one call at mount, plain axios not React
  // Query so we don't need to bring the whole inbox list into memory here.
  useEffect(() => {
    let cancelled = false;
    api.get("/vendor/inquiries?limit=1")
      .then(({ data }) => {
        if (cancelled) return;
        setInquiries({
          unread: data.unread_count || 0,
          total: data.total_returned || 0,
          loaded: true,
        });
      })
      .catch(() => {
        if (!cancelled) setInquiries((prev) => ({ ...prev, loaded: true }));
      });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => { load(); }, []);

  const pay = async (applicationId) => {
    setPayingId(applicationId);
    try {
      const { data } = await api.post("/payments/booth-fee", {
        application_id: applicationId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : formatApiErrorDetail(detail);
      toast.error(msg || "Payment failed to start");
      setPayingId(null);
    }
  };

  const createInvoiceFromApp = async (applicationId) => {
    try {
      const { data } = await api.post("/documents/invoices/from-booking", {
        application_id: applicationId,
      });
      toast.success(`Draft invoice ${data.number} created. Opening…`);
      setTimeout(() => { window.location.href = "/vendor/documents"; }, 400);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Pro+ gate — route them to pricing with a soft hint
      if (e.response?.status === 402) {
        toast.error("Invoicing is a Maker Pro+ feature.", {
          action: { label: "See Pro+", onClick: () => { window.location.href = "/pricing"; } },
        });
      } else {
        const msg = typeof detail === "object" ? detail.message : formatApiErrorDetail(detail);
        toast.error(msg || "Could not create invoice");
      }
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>Vendor dashboard</div>
          <h1 className="font-serif text-5xl mt-2">Your applications.</h1>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/vendor/profile" data-testid="link-profile" className="btn-outline px-5 py-2.5 rounded-full">Edit profile</Link>
          <Link to="/vendor/templates" data-testid="link-templates" className="btn-outline px-5 py-2.5 rounded-full">Templates</Link>
          <Link to="/events" data-testid="link-browse" className="btn-primary px-5 py-2.5 rounded-full">Browse events</Link>
        </div>
      </div>

      {user && user.on_trial && (
        <div
          data-testid="trial-active-banner"
          className="mt-8 card-warm rounded-sm p-5 flex items-center justify-between gap-4 flex-wrap"
          style={{
            background: "linear-gradient(135deg, rgba(31,93,52,0.14), rgba(201,162,75,0.06))",
            borderLeft: "3px solid var(--forest)",
          }}
        >
          <div className="flex-1 min-w-[260px]">
            <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--forest)" }}>
              <Zap className="h-3 w-3" /> Maker Pro trial · {user.trial_days_remaining} day{user.trial_days_remaining === 1 ? "" : "s"} left
            </div>
            <div className="font-serif text-2xl mt-1.5">You're on the good stuff.</div>
            <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
              Business Assistant, Bookkeeping, receipt AI, mileage log — all unlocked. Keep them going by upgrading before the trial ends.
            </p>
          </div>
          <Link
            to="/pricing"
            data-testid="trial-active-cta"
            className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap"
          >
            Upgrade to keep Pro <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {user && user.plan !== "pro" && !user.on_trial && (
        <div
          data-testid="upgrade-banner"
          className="mt-8 card-warm rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap"
          style={{
            background: "linear-gradient(135deg, rgba(31,93,52,0.10), rgba(31,93,52,0.03))",
            borderLeft: "3px solid var(--terracotta)",
          }}
        >
          <div className="flex-1 min-w-[260px]">
            <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
              <Sparkles className="h-3 w-3" /> {user.trial_available ? "Try it for free" : "Grow faster"}
            </div>
            <div className="font-serif text-2xl mt-1.5">
              {user.trial_available ? "Try Maker Pro for 7 days" : "Unlock Maker Pro"}
            </div>
            <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
              {user.trial_available
                ? "Free trial · no card required. Get Business Assistant, Bookkeeping, receipt AI, mileage tracking, and Schedule C exports."
                : "Unlimited applications, priority in the weekly digest, verified badge boost, and 20 portfolio photos."}
              {user.pending_promo_code ? (
                <span className="ml-1"><strong style={{ color: "var(--terracotta)" }}>Your {user.pending_promo_code} code will auto-apply.</strong></span>
              ) : null}
            </p>
          </div>
          <Link
            to={user.trial_available ? "/vendor/assistant" : "/pricing"}
            data-testid="upgrade-banner-cta"
            className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap"
          >
            {user.trial_available ? "Start free trial" : "See pricing"} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <ReferralsCard variant="vendor" />
      <div className="mt-4">
        <NonprofitStatusCard />
      </div>

      <Link
        to="/vendor/assistant"
        data-testid="assistant-entry-card"
        className="mt-8 card-warm rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap hover:shadow-md transition-shadow"
        style={{
          background: "linear-gradient(135deg, rgba(31,93,52,0.14), rgba(201,162,75,0.06))",
          borderLeft: "3px solid var(--forest)",
        }}
      >
        <div className="flex-1 min-w-[260px]">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
            <Sparkles className="h-3 w-3" /> AI co-founder
            {user && user.plan !== "pro" && !user.on_trial && <ProBadge testId="assistant-pro-badge" />}
          </div>
          <div className="font-serif text-2xl mt-1.5">Business Assistant</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Chat with an AI that knows your profile. Draft launch emails, IG posts, product descriptions and get instant pricing math — all in one place.
          </p>
        </div>
        <span
          className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap"
        >
          {user && user.plan !== "pro" && !user.on_trial ? "See Pro" : "Open Assistant"} <ArrowRight className="h-4 w-4" />
        </span>
      </Link>

      <Link
        to="/vendor/bookkeeping"
        data-testid="bookkeeping-entry-card"
        className="mt-4 card-warm rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap hover:shadow-md transition-shadow"
        style={{
          background: "linear-gradient(135deg, rgba(201,162,75,0.16), rgba(31,93,52,0.05))",
          borderLeft: "3px solid var(--gold)",
        }}
      >
        <div className="flex-1 min-w-[260px]">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--gold)" }}>
            <BookOpen className="h-3 w-3" /> Books & taxes
            {user && user.plan !== "pro" && !user.on_trial && <ProBadge testId="bookkeeping-pro-badge" />}
          </div>
          <div className="font-serif text-2xl mt-1.5">Bookkeeping</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Log expenses and income, auto-import booth fees, snap a receipt to record it, and export a Schedule C summary at tax time.
          </p>
        </div>
        <span className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap">
          {user && user.plan !== "pro" && !user.on_trial ? "See Pro" : "Open Books"} <ArrowRight className="h-4 w-4" />
        </span>
      </Link>

      <Link
        to="/vendor/inquiries"
        data-testid="inquiries-entry-card"
        className="mt-4 card-warm rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap hover:shadow-md transition-shadow"
        style={{
          background: "linear-gradient(135deg, rgba(31,93,52,0.08), rgba(201,162,75,0.06))",
          borderLeft: "3px solid var(--moss)",
        }}
      >
        <div className="flex-1 min-w-[260px]">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--moss)" }}>
            <Inbox className="h-3 w-3" /> Book Now inbox
            {!inquiries.loaded && (
              <span
                data-testid="inquiries-loading-chip"
                className="ml-1 h-3 w-10 rounded-full inline-block"
                style={{ backgroundColor: "var(--sand)", opacity: 0.6 }}
              />
            )}
            {inquiries.loaded && inquiries.unread > 0 && (
              <span
                data-testid="inquiries-unread-badge"
                className="ml-1 inline-flex items-center rounded-full px-2 py-0 text-[10px] font-semibold"
                style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
              >
                {inquiries.unread} new
              </span>
            )}
          </div>
          <div className="font-serif text-2xl mt-1.5">Inquiries</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            {!inquiries.loaded
              ? "Loading your inbox…"
              : inquiries.unread > 0
              ? `You have ${inquiries.unread} unread message${inquiries.unread === 1 ? "" : "s"} from customers who scanned your booth QR.`
              : "Messages sent through the Book Now button on your public profile land here. Reply straight from your inbox — their email is set as reply-to."}
          </p>
        </div>
        <span className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap">
          Open inbox <ArrowRight className="h-4 w-4" />
        </span>
      </Link>

      <Link
        to="/vendor/documents"
        data-testid="documents-entry-card"
        className="mt-4 card-warm rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap hover:shadow-md transition-shadow"
        style={{
          background: "linear-gradient(135deg, rgba(107,47,186,0.14), rgba(249,115,22,0.08))",
          borderLeft: "3px solid var(--terracotta)",
        }}
      >
        <div className="flex-1 min-w-[260px]">
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--terracotta)" }}>
            <FileText className="h-3 w-3" /> Documents · Pro+
            {user && user.plan_tier !== "pro_plus" && (
              <span
                data-testid="documents-pro-plus-badge"
                className="ml-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0 text-[10px] font-medium tracking-wider uppercase"
                style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
              >
                <Lock className="h-2.5 w-2.5" /> Pro+
              </span>
            )}
          </div>
          <div className="font-serif text-2xl mt-1.5">Invoices & Contracts</div>
          <p className="mt-1 text-sm max-w-xl" style={{ color: "var(--ink-soft)" }}>
            Send branded invoices and contracts your clients can pay or sign online. PDF, Stripe-hosted pay link, light e-signature — all in one workspace.
          </p>
        </div>
        <span className="btn-primary px-5 py-2.5 rounded-full inline-flex items-center gap-2 whitespace-nowrap">
          {user && user.plan_tier !== "pro_plus" ? "See Pro+" : "Open Documents"} <ArrowRight className="h-4 w-4" />
        </span>
      </Link>

      <VendorScorecardCard />

      <AIRecommendationsPanel />

      <div className="mt-10">
        {loading ? (
          <div className="text-center py-20 font-serif text-2xl">Loading…</div>
        ) : apps.length === 0 ? (
          <div className="card-warm p-12 rounded-sm text-center">
            <div className="font-serif text-3xl">No applications yet.</div>
            <div className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>Discover events and apply with your portfolio.</div>
            <Link to="/events" className="mt-6 inline-block btn-primary px-6 py-3 rounded-full">Browse events</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="applications-grid">
            {apps.map((a) => (
              <div key={a.id} data-testid={`application-${a.id}`} className="card-warm rounded-sm p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="overline" style={{ color: "var(--ink-soft)" }}>{a.event_city} · {a.event_date}</div>
                    <div className="font-serif text-2xl mt-1">{a.event_title}</div>
                  </div>
                  <span className={`status-badge ${statusClass(a.status)}`} data-testid={`status-${a.id}`}>{a.status}</span>
                </div>
                <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-soft)" }}><span className="overline">Pitch</span><br />{a.pitch}</p>
                {a.occurrence_dates && a.occurrence_dates.length > 1 && (
                  <div className="mt-3" data-testid={`app-occurrences-${a.id}`}>
                    <span className="overline block mb-1" style={{ color: "var(--ink-soft)" }}>
                      Attending {a.occurrence_dates.length} dates
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {a.occurrence_dates.slice(0, 12).map((d) => (
                        <span key={d} className="text-[11px] px-2 py-0.5 rounded"
                          style={{ backgroundColor: "var(--paper-warm)",
                                   border: "1px solid var(--sand)",
                                   color: "var(--ink-soft)" }}>
                          {new Date(d + "T00:00:00").toLocaleDateString(undefined,
                            { month: "short", day: "numeric" })}
                        </span>
                      ))}
                      {a.occurrence_dates.length > 12 && (
                        <span className="text-[11px]" style={{ color: "var(--ink-soft)" }}>
                          +{a.occurrence_dates.length - 12} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {a.status === "waitlisted" && (
                  <div
                    className="mt-4 rounded-sm p-3 border text-xs"
                    style={{
                      backgroundColor: "rgba(196,139,46,0.06)",
                      borderColor: "rgba(196,139,46,0.3)",
                      color: "var(--ink-soft)",
                    }}
                    data-testid={`application-waitlisted-${a.id}`}
                  >
                    You&apos;re on the waitlist — this event was already full when you applied.
                    Sit tight: if an approved vendor drops out, the organizer can promote you
                    from the waitlist and you&apos;ll get a notification.
                  </div>
                )}
                {a.status === "cancelled" && (
                  <div
                    className="mt-4 rounded-sm p-3 border text-xs"
                    style={{
                      backgroundColor: "rgba(31,93,52,0.06)",
                      borderColor: "rgba(31,93,52,0.3)",
                      color: "var(--ink-soft)",
                    }}
                    data-testid={`application-cancelled-${a.id}`}
                  >
                    The organizer cancelled this event.
                    {a.payment_status === "refunded"
                      ? " Your booth fee has been refunded in full to your card."
                      : a.payment_status === "paid"
                        ? " A refund is being processed."
                        : ""}
                  </div>
                )}
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                    Booth fee: {a.booth_fee > 0 ? `$${a.booth_fee.toFixed(2)}` : "Free"}
                    {a.booth_fee > 0 && (
                      <span className="ml-2">· <span className={`status-badge ${a.payment_status === "paid" ? "status-paid" : a.payment_status === "refunded" ? "status-rejected" : "status-pending"}`}>{a.payment_status}</span></span>
                    )}
                  </div>
                  {a.status === "approved" && a.booth_fee > 0 && a.payment_status !== "paid" && a.payment_status !== "refunded" && (
                    <Button data-testid={`pay-${a.id}`} onClick={() => pay(a.id)} disabled={payingId === a.id} className="btn-primary rounded-full">
                      {payingId === a.id ? "Redirecting…" : `Pay $${a.booth_fee.toFixed(2)}`}
                    </Button>
                  )}
                  {a.status === "approved" && (
                    <Button
                      data-testid={`invoice-from-app-${a.id}`}
                      onClick={() => createInvoiceFromApp(a.id)}
                      variant="outline"
                      className="rounded-full inline-flex items-center gap-1.5"
                      title="Create a prefilled invoice for this booking"
                    >
                      <FileText className="h-4 w-4" /> Invoice
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PendingRatingsList />

      <div className="mt-10">
        <PushToggle />
      </div>
    </div>
  );
}
